// @flow
import type { Grade } from 'core/types'
import type { UserRole, PupilRole, PossibleAccountFields, ProfileUpdateUser, UserType } from '../types'
import type { User } from '../entities'

const gradeMap: { [key: string]: PupilRole } = {
  '11': '11 класс',
  '10': '10 класс',
  '9': '9 класс',
  '8': '8 класс',
  '7': '7 класс',
  '6': '6 класс',
  '5': '5 класс',
  '4': '4 класс',
  '3': '3 класс',
  '2': '2 класс',
  '1': '1 класс',
}

/**
 * Возвращает тектовое представление класса пользователя
 * @param {string} index индекс класса
 * @return {PupilRole} '11 класс|10 класс|...'
 */
export const getGrade = (index: number): PupilRole => gradeMap[`${index}`] || gradeMap['1']

/**
 * Возвращает тектовое представление роли / класса пользователя
 *
 * @param {{ grade: Grade }} { grade }
 * @returns {UserRole}
 */
export const getPupilRole = ({ grade }: { grade: Grade }): UserRole => {
  if (grade.index === 0) {
    return 'Дошкольник'
  }

  if (!grade.index) return 'Выпускник'

  const { index } = grade

  return getGrade(index)
}

/**
 * Возвращает тектовое представление роли юзера
 *
 * @param {{
 *   isCoach: boolean,
 *   isAgent: boolean,
 *   isParent: boolean,
 *   type: UserType,
 *   grade: Grade,
 * }}
 * @returns {UserRole} 'Ученик|Репетитор|Родитель|Выпускник|11 класс|...'
 */
export const getUserRole = ({
  isCoach,
  isAgent,
  isParent,
  type,
  grade,
}: {
  isCoach: boolean,
  isAgent: boolean,
  isParent: boolean,
  type: UserType,
  grade: Grade,
}): UserRole => {
  if (isCoach) return 'Репетитор'
  if (isAgent) return 'Учитель'
  if (isParent) return 'Родитель'

  switch (type) {
    case 'pupil':
      return getPupilRole({ grade })

    case 'agent':
      return 'Учитель'

    default:
      return 'Ученик'
  }
}

/**
 * Возвращает фамилию и инициалы юзера
 * @param {User} user
 * @return {string} 'Пупкин В.'
 */
export const getUserInitials = (user: User): string =>
  `${user.lastName} ${user.firstName ? `${user.firstName[0].toUpperCase()}.` : ''} ${
    user.middleName !== '' && user.middleName !== null ? ` ${user.middleName[0].toUpperCase()}.` : ''
  }`

const getValue = <T>(field: { value: T } | void): T | void => field?.value ?? undefined
const dealWithFalsyValue = <T>(val: T | void): T | string | void => (val === undefined ? val : val ?? '')

export const prepareUserProfileData = ({
  grade_id,
  skype,
  school_id,
  school_country_id,
  school_region_id,
  school_city_id,
  postal_index,
  country_id,
  region_id,
  city_id,
  street,
  house,
  building,
  room,
  caption,
  profession,
  phone,
  parent_phone,
  birthday,
  middle_name,
  fake_user,
  is_customer,
  start_teaching_year,
  last_certification_date,
  school_entry_year,
  parent_sms_required,
  ...data
}: $Shape<PossibleAccountFields> = {}): $Shape<ProfileUpdateUser> => ({
  ...data,
  grade_id: getValue(grade_id),
  user_info_attributes: {
    skype: dealWithFalsyValue(skype),
    caption: typeof caption === 'string' && caption !== '' ? caption : null,
    parent_sms_required: parent_sms_required || false,
    parent_phone: dealWithFalsyValue(parent_phone),
    start_teaching_year: dealWithFalsyValue(start_teaching_year),
    last_certification_date:
      // can be `false` if certification not been yet
      last_certification_date === false ? last_certification_date : dealWithFalsyValue(last_certification_date),
    school_entry_year: getValue(school_entry_year),
  },
  profession,
  school_attributes: {
    id: getValue(school_id),
    address_attributes: {
      country_id: getValue(school_country_id),
      region_id: getValue(school_region_id),
      city_id: getValue(school_city_id),
    },
  },
  address_attributes: {
    index: postal_index,
    country_id: getValue(country_id),
    region_id: getValue(region_id),
    city_id: getValue(city_id),
    street,
    house,
    building,
    room,
  },
  phone: dealWithFalsyValue(phone),
  birthday: dealWithFalsyValue(birthday),
  middle_name: dealWithFalsyValue(middle_name),
  fake_user: fake_user || (data.fake_email === true && !data.phone_confirmed),
  is_customer,
})
